import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Chip, Stack, tabClasses, Typography, useTheme } from '@mui/material';

import { useGetTalentProcesses } from '@/api/generated';
import CommonDialog from '@/components/Dialog/CommonDialog';
import CommonDialogContent from '@/components/Dialog/CommonDialogContent';
import CommonDialogTitle from '@/components/Dialog/CommonDialogTitle';
import Loader from '@/components/Loader/Loader';
import { TabPanel, VerticalTab, VerticalTabs } from '@/components/Tabs';
import { useTalentPermissions } from '@/hooks/permissions/useTalentPermissions';
import { ReadonlyProvider } from '@/hooks/useReadonly';

import AdditionalInfo from './AdditionalInfo';
import AddToProcesses from './AddToProcess';
import BasicInfo from './BasicInfo';
import { getAdditionalInfoKey, TalentModalEditSection, TalentModalKey } from './constants';

const TalentEditInfoModal = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation(['talents', 'validation', 'common']);
  const { spacing, palette } = useTheme();
  const editInfoKey = searchParams.get(TalentModalKey);
  const { talentId } = useParams<{ talentId: string }>();
  const { data: processesResponse, isLoading: isProcessesLoading } = useGetTalentProcesses(talentId!, {
    query: { enabled: !!talentId, suspense: false }
  });
  const isLoadedRef = useRef(false);

  useEffect(() => {
    if (isLoadedRef.current || isProcessesLoading) return;
    const includesSection = processesResponse?.processes
      ?.map(getAdditionalInfoKey)
      .concat(
        Object.values(TalentModalEditSection).filter(section => section !== TalentModalEditSection.ADDITIONAL_INFO)
      )
      .some(editKey => editKey === editInfoKey);

    if (searchParams.has(TalentModalKey) && !includesSection) {
      searchParams.set(TalentModalKey, TalentModalEditSection.BASIC_INFO);
      setSearchParams(searchParams);
    }

    isLoadedRef.current = true;
  }, [editInfoKey, isProcessesLoading, processesResponse, searchParams, setSearchParams]);

  const onClose = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const { value: { canUpdate: canUpdateTalent } = {} } = useTalentPermissions({
    variables: { talentId: talentId! }
  });

  return (
    <CommonDialog open={!!editInfoKey} onClose={onClose}>
      <CommonDialogTitle onClose={onClose}>
        <Typography component="h2" variant="h3">
          {t('layouts.talentEditModal.title')}
        </Typography>
      </CommonDialogTitle>
      <CommonDialogContent>
        <Stack
          direction="row"
          flexBasis={0}
          flexGrow={1}
          sx={{
            borderTop: `1px solid ${palette.grey[300]}`,
            borderColor: palette.grey[300]
          }}
        >
          <Box
            sx={{
              width: spacing(25),
              paddingX: 2,
              paddingY: 3,
              borderEndStartRadius: spacing(0.5),
              backgroundColor: palette.background.light
            }}
          >
            <VerticalTabs
              value={editInfoKey || TalentModalEditSection.BASIC_INFO}
              onChange={(_e, tabKey) => {
                const searchParams = new URLSearchParams();
                searchParams.set(TalentModalKey, tabKey);
                setSearchParams(searchParams);
              }}
            >
              <VerticalTab
                value={TalentModalEditSection.BASIC_INFO}
                label={t('layouts.talentEditModal.tabs.basicInfo')}
              />

              <VerticalTab
                value={TalentModalEditSection.ADD_TO_PROCESS}
                label={t('layouts.talentEditModal.tabs.addToProcess')}
              />

              {processesResponse?.processes?.map(process => (
                <VerticalTab
                  sx={{ whiteSpace: 'normal', textAlign: 'start' }}
                  key={process.id}
                  value={getAdditionalInfoKey(process)}
                  label={
                    <>
                      {t('layouts.talentEditModal.tabs.additionalInfo')}
                      <Chip variant="tag" label={process.name} sx={{ marginTop: spacing(0.5) }} />
                      <Typography
                        component="span"
                        variant="body4"
                        sx={{
                          marginTop: spacing(0.5),
                          [`${tabClasses.root}:not(${tabClasses.selected}) &`]: { color: palette.grey[600] }
                        }}
                      >
                        {t('common:optional')}
                      </Typography>
                    </>
                  }
                />
              ))}
            </VerticalTabs>
          </Box>

          {isProcessesLoading || !editInfoKey ? (
            <Loader sx={{ mx: 'auto' }} />
          ) : (
            <ReadonlyProvider readonly={!canUpdateTalent}>
              <Box flexGrow={1}>
                <TabPanel currentValue={editInfoKey} value={TalentModalEditSection.BASIC_INFO}>
                  <BasicInfo />
                </TabPanel>
                <TabPanel currentValue={editInfoKey} value={TalentModalEditSection.ADD_TO_PROCESS}>
                  <AddToProcesses talentId={talentId} />
                </TabPanel>
                {processesResponse?.processes?.map((process, i) => (
                  <TabPanel
                    key={process.id}
                    currentValue={editInfoKey}
                    value={getAdditionalInfoKey(processesResponse.processes![i])}
                  >
                    {talentId && <AdditionalInfo process={process} talentId={talentId} />}
                  </TabPanel>
                ))}
              </Box>
            </ReadonlyProvider>
          )}
        </Stack>
      </CommonDialogContent>
    </CommonDialog>
  );
};

export default TalentEditInfoModal;
